import React, { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate, useParams, Link } from "react-router-dom";
import styles from "./DocumentViewPage.module.css";

import Synopsis from "./sidebarCards/Synopsis";
import Modules from "./sidebarCards/modules";
import DocumentsTab from "./tabs/DocumentsTab";
import OwnerTab from "./tabs/OwnerTab";
import RefDoc from "./sidebarCards/RefDoc";
import ChallengeStandard from "./sidebarCards/ChallengeStandard";
import TagsWidget from "./sidebarCards/TagsWidget";
import DocumentHeader from "./header/DocumentHeader";
import AttachmentPopup from "./attachments/attachmentPopUp";
import VideoBriefing from "./videoBriefing/videobriefing";
import OtherEditions from "./sidebarCards/otherEditions";
import DocumentVariations from "./variations/documentVariations";
import DeleteStandardPopUp from "./modals/DeleteStandardPopUp";
import NewStandSideBar from "../NewStandard/NewStandSideBar";
import ProgressSpinnerComponent from "../Auth/ProgressSpinner";
import useTrackerVariations from "../../api/useTrackerVariationsApi";

import useHierarchyApi from "../../api/useHierarchyApi";
import { useAuthenticatedApi } from "../../auth";
import { useUserCapabilities } from "../../auth.capabilities";
import useFilesApi from "../../api/filesApi";
import {
  deleteStandard,
  getDocumentForEdit,
  createNewIssue,
  DUPLICATE_ISSUE_MESSAGE,
  resolveReferenceNumbers,
  findDuplicateStandardIssue,
  getNextIssueNumberForReference,
  updateStandard,
} from "../../api/newStandardApi";
import {
  getFavouriteStandards,
  favoriteStandard,
  removeFavoriteStandard,
} from "../../api/favouritesApi";
import {
  applyResolvedReferenceNumbersToEditStates,
  buildEditStatesFromSummary,
  buildUpdatePayload,
  collectEditStateUnresolvedReferenceIds,
  DEFAULT_OVERVIEW_STATE,
  DEFAULT_OTHER_EDITIONS_STATE,
  DEFAULT_PDSS_STATE,
  DEFAULT_REFERENCE_STATE,
  PAGE_MODE_COPY_ISSUE,
  isDeletedStatusValue,
  isDeletedSummary,
  mergeEditStatesWithResolvedNumbers,
  resolveCopiedIssueNumber,
  resolveCreatedStandardId,
} from "../NewStandard/editShared";

const OverviewSection = lazy(() => import("../NewStandard/sections/OverviewSection"));
const OtherEditionsSection = lazy(() => import("../NewStandard/sections/OtherEditionsSection"));
const PdssMetadataSection = lazy(() => import("../NewStandard/sections/PdssMetadataSection"));
const ReferenceDocumentsSection = lazy(() => import("../NewStandard/sections/ReferenceDocumentsSection"));

const SECTION_MAP = {
  overview: OverviewSection,
  "other-editions": OtherEditionsSection,
  "pdss-metadata": PdssMetadataSection,
  "reference-documents": ReferenceDocumentsSection,
};

const PAGE_MODE_EDIT_EXISTING = "edit-existing";
const PAGE_MODE_VIEW = "view";
const EDIT_VIEW_MODE = "edit";
const STANDARD_DOWNLOADS_STORAGE_KEY = "steeringGroupStandards";
const EMPTY_ARRAY = [];
const sameStateValue = (a, b) => JSON.stringify(a) === JSON.stringify(b);

const clampToStringId = (v) => {
  if (v == null) return "";
  return String(v).trim();
};

const normalize = (v) => String(v ?? "").trim();
const normLower = (v) => normalize(v).toLowerCase();

const readDownloadQueueItems = () => {
  try {
    const parsed = JSON.parse(
      window.localStorage.getItem(STANDARD_DOWNLOADS_STORAGE_KEY),
    );
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const getDownloadQueueId = (item) =>
  String(item?.id ?? item?.standardId ?? item?.referenceNumber ?? "").trim();

const getRelatedStandardId = (item) =>
  clampToStringId(item?.standardId ?? item?.id);

const getPublicationDateValue = (item) =>
  item?.issueDate ??
  item?.publicationDate ??
  item?.summary?.issueDate ??
  item?.summary?.publicationDate;

const sameCrumb = (a, b) => {
  if (!a || !b) return false;

  const aFolder = a.folderId != null ? String(a.folderId) : "";
  const bFolder = b.folderId != null ? String(b.folderId) : "";
  if (aFolder && bFolder && aFolder === bFolder) return true;

  const aCat = a.categoryId != null ? String(a.categoryId) : "";
  const bCat = b.categoryId != null ? String(b.categoryId) : "";
  if (aCat && bCat && aCat === bCat) return true;

  return normLower(a.title) === normLower(b.title);
};

const dedupeConsecutive = (arr) => {
  const out = [];
  for (const item of arr) {
    const prev = out[out.length - 1];
    if (prev && sameCrumb(prev, item)) continue;
    out.push(item);
  }
  return out;
};

const waitForNextPaint = () =>
  new Promise((resolve) => {
    window.requestAnimationFrame(() => {
      window.setTimeout(resolve, 0);
    });
  });

const MIN_SAVE_OVERLAY_MS = 500;

const waitForMinimumOverlayTime = async (startTime) => {
  const elapsed = Date.now() - startTime;
  const remaining = MIN_SAVE_OVERLAY_MS - elapsed;
  if (remaining > 0) {
    await new Promise((resolve) => window.setTimeout(resolve, remaining));
  }
};

const firstNonEmptyArray = (...values) =>
  values.find((value) => Array.isArray(value) && value.length) ?? [];

const resolveDocumentLoadError = (status, apiMessage, documentId) => {
  if (apiMessage) return apiMessage;
  if (status === 403) {
    return "This standard has been deleted or you do not have permission to view it.";
  }
  if (status === 404) {
    return "This standard no longer exists.";
  }
  return `No document data found for ID ${documentId}`;
};

export default function DocumentViewPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { id } = useParams();

  const [documentData, setDocumentData] = useState(null);
  const [documentError, setDocumentError] = useState("");
  const [loading, setLoading] = useState(true);

  const [isFav, setIsFav] = useState(false);
  const [favResolved, setFavResolved] = useState(false);
  const [favBusy, setFavBusy] = useState(false);

  const [activeTab, setActiveTab] = useState("overview");
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [files, setFiles] = useState({});
  const [filesLoading, setFilesLoading] = useState(false);
  const [filesError, setFilesError] = useState("");
  const [pageMode, setPageMode] = useState(PAGE_MODE_VIEW);
  const [editPageMode, setEditPageMode] = useState(PAGE_MODE_EDIT_EXISTING);
  const [activeEditSection, setActiveEditSection] = useState("overview");
  const [editSummary, setEditSummary] = useState(null);
  const [originalEditSummary, setOriginalEditSummary] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState("");
  const [saveLoading, setSaveLoading] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [saveInfo, setSaveInfo] = useState("");
  const [copyIssueLoading, setCopyIssueLoading] = useState(false);
  const [copyIssueError, setCopyIssueError] = useState("");
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [hasSavedEdit, setHasSavedEdit] = useState(false);
  const [attachmentInfo, setAttachmentInfo] = useState("");
  const [overviewState, setOverviewState] = useState(null);
  const [otherEditionsState, setOtherEditionsState] = useState(null);
  const [pdssState, setPdssState] = useState(null);
  const [referenceState, setReferenceState] = useState(null);
  const [showStickyEditActions, setShowStickyEditActions] = useState(false);
  const [otherEditionPublicationDates, setOtherEditionPublicationDates] =
    useState({});

  const { getDocument, getDocumentNavigation } = useHierarchyApi();
  const { getStandardFiles } = useFilesApi();
  const { callProtectedApi, apiBaseUrl } = useAuthenticatedApi();
  const caps = useUserCapabilities() || {};
  const hasAdminRights = caps.hasAdminRights === true;
  const callRef = useRef(callProtectedApi);
  const resolveReferenceRequestIdRef = useRef(0);
  const resolvedEditStatesRef = useRef({
    otherEditionsState: DEFAULT_OTHER_EDITIONS_STATE,
    referenceState: DEFAULT_REFERENCE_STATE,
  });
  const [apiFallbackNav, setApiFallbackNav] = useState([]);

  useEffect(() => {
    callRef.current = callProtectedApi;
  }, [callProtectedApi]);

  const resolveFavouritesPage = (res) => {
    if (Array.isArray(res?.items))
      return { items: res.items, total: Number(res?.total) || 0 };
    if (Array.isArray(res?.data?.items))
      return { items: res.data.items, total: Number(res?.data?.total) || 0 };
    return { items: [], total: 0 };
  };

  const isFavouriteFromGet = async (currentStandardId) => {
    if (!apiBaseUrl || !currentStandardId) return null;

    const target = String(currentStandardId);
    const pageSize = 10;
    const maxPages = 200;
    let page = 1;

    try {
      while (page <= maxPages) {
        const res = await getFavouriteStandards(
          apiBaseUrl,
          callRef.current,
          page,
          pageSize,
        );
        const { items, total } = resolveFavouritesPage(res);

        const match = items.some((item) => {
          const itemId = String(item?.standardId ?? item?.id ?? "");
          return itemId === target;
        });
        if (match) return true;

        const reachedEndByTotal = total > 0 && page * pageSize >= total;
        const reachedEndByItems = items.length < pageSize;
        if (reachedEndByTotal || reachedEndByItems) break;

        page += 1;
      }

      return false;
    } catch {
      return null;
    }
  };

  useEffect(() => {
    if (!id) return;

    let alive = true;

    const fetchDocument = async () => {
      setLoading(true);
      setDocumentError("");
      setFavResolved(false);
      try {
        const response = await getDocument(id);
        if (!alive) return;

        const apiMessage =
          typeof response?.message === "string" ? response.message.trim() : "";
        const isFailure = response?.success === false;

        if (isFailure || !response) {
          setDocumentData(null);
          setDocumentError(resolveDocumentLoadError(null, apiMessage, id));
          setIsFav(false);
          setFavResolved(true);
          return;
        }
        setDocumentData(response);

        const favFromApi =
          Boolean(response?.favorite) ||
          Boolean(response?.summary?.favorite) ||
          Boolean(response?.summary?.favorited);

        setIsFav(favFromApi);
      } catch (err) {
        if (!alive) return;
        const status = err?.response?.status;
        const apiMessage =
          typeof err?.response?.data?.message === "string"
            ? err.response.data.message.trim()
            : "";
        setDocumentData(null);
        setDocumentError(resolveDocumentLoadError(status, apiMessage, id));
        setIsFav(false);
        setFavResolved(true);
      } finally {
        if (alive) setLoading(false);
      }
    };

    fetchDocument();

    return () => {
      alive = false;
    };
  }, [id, getDocument]);

  useEffect(() => {
    setActiveTab("overview");
    setPageMode(PAGE_MODE_VIEW);
    setEditPageMode(PAGE_MODE_EDIT_EXISTING);
    setActiveEditSection("overview");
    setEditSummary(null);
    setOriginalEditSummary(null);
    setOverviewState(null);
    setOtherEditionsState(null);
    setPdssState(null);
    setReferenceState(null);
    setOtherEditionPublicationDates({});
    setEditError("");
    setSaveError("");
    setSaveInfo("");
    setCopyIssueError("");
    setHasSavedEdit(false);
    setAttachmentInfo("");
  }, [id]);

  useEffect(() => {
    const copiedIssueNotice = normalize(location.state?.copiedIssueNotice);
    if (!copiedIssueNotice) return;
    setSaveInfo(copiedIssueNotice);
  }, [location.state?.copiedIssueNotice]);

  useEffect(() => {
    resolvedEditStatesRef.current = {
      otherEditionsState: otherEditionsState ?? DEFAULT_OTHER_EDITIONS_STATE,
      referenceState: referenceState ?? DEFAULT_REFERENCE_STATE,
    };
  }, [otherEditionsState, referenceState]);

  const standardId = useMemo(() => {
    const fromRoute = clampToStringId(id);
    if (fromRoute) return fromRoute;

    const fromSummary = clampToStringId(
      documentData?.summary?.standardId ?? documentData?.summary?.id,
    );
    if (fromSummary) return fromSummary;

    const fromRoot = clampToStringId(
      documentData?.standardId ?? documentData?.id,
    );
    if (fromRoot) return fromRoot;

    return "";
  }, [id, documentData]);
  const variationsStandardNumber = useMemo(
    () =>
      documentData?.summary?.referenceNumber ??
      documentData?.summary?.reference ??
      documentData?.referenceNumber ??
      "",
    [documentData],
  );
  const variationsIssueNo = useMemo(
    () =>
      documentData?.summary?.issueNo ??
      documentData?.summary?.issue ??
      documentData?.issueNo ??
      "",
    [documentData],
  );
  const {
    variationPendingChange,
    derogation,
    temporaryVariation,
  } = useTrackerVariations({
    standardId,
    standardNumber: variationsStandardNumber,
    issueNo: variationsIssueNo,
    enabled: pageMode !== EDIT_VIEW_MODE,
  });
  const isDeletedDocument = useMemo(
    () =>
      isDeletedSummary(documentData?.summary) ||
      isDeletedStatusValue(documentData?.statusId) ||
      isDeletedStatusValue(documentData?.status),
    [documentData],
  );
  const isDeletedEditStatus = useMemo(
    () =>
      isDeletedStatusValue(overviewState?.status) ||
      isDeletedSummary(editSummary) ||
      isDeletedSummary(originalEditSummary),
    [editSummary, originalEditSummary, overviewState?.status],
  );
  const isDeletedRestoreMode = pageMode === EDIT_VIEW_MODE && isDeletedEditStatus;
  const activeEditPanelKey = isDeletedRestoreMode ? "overview" : activeEditSection;
  const ActiveEditPanel = SECTION_MAP[activeEditPanelKey];

  useEffect(() => {
    if (loading) return;
    if (!documentData) return;
    if (!standardId) {
      setFavResolved(true);
      return;
    }

    let alive = true;
    setFavResolved(false);

    const fetchFavouriteState = async () => {
      const favFromList = await isFavouriteFromGet(standardId);
      if (!alive) return;

      if (typeof favFromList === "boolean") {
        setIsFav(favFromList);
      }

      setFavResolved(true);
    };

    fetchFavouriteState();

    return () => {
      alive = false;
    };
  }, [loading, documentData, standardId, apiBaseUrl]);

  const relatedDocs = Array.isArray(documentData?.related)
    ? documentData.related
    : EMPTY_ARRAY;
  const supersededDocs = Array.isArray(documentData?.superseded)
    ? documentData.superseded
    : EMPTY_ARRAY;
  const replacedDocs = Array.isArray(documentData?.replaced)
    ? documentData.replaced
    : EMPTY_ARRAY;
  const otherEditionDocs = useMemo(
    () => [...supersededDocs, ...replacedDocs],
    [supersededDocs, replacedDocs],
  );

  useEffect(() => {
    if (!otherEditionDocs.length) {
      setOtherEditionPublicationDates((prev) =>
        Object.keys(prev).length ? {} : prev,
      );
      return;
    }

    const missingDateIds = Array.from(
      new Set(
        otherEditionDocs
          .filter((item) => !getPublicationDateValue(item))
          .map(getRelatedStandardId)
          .filter(Boolean),
      ),
    ).filter(
      (relatedId) =>
        !Object.prototype.hasOwnProperty.call(
          otherEditionPublicationDates,
          relatedId,
        ),
    );

    if (!missingDateIds.length) return;

    let alive = true;

    const fetchMissingPublicationDates = async () => {
      const entries = await Promise.all(
        missingDateIds.map(async (relatedId) => {
          try {
            const relatedDoc = await getDocument(relatedId);
            return [relatedId, getPublicationDateValue(relatedDoc)];
          } catch {
            return [relatedId, null];
          }
        }),
      );

      if (!alive) return;

      setOtherEditionPublicationDates((prev) => {
        const next = { ...prev };
        entries.forEach(([relatedId, publicationDate]) => {
          next[relatedId] = publicationDate || "";
        });
        return next;
      });
    };

    fetchMissingPublicationDates();

    return () => {
      alive = false;
    };
  }, [getDocument, otherEditionDocs, otherEditionPublicationDates]);

  const supersededDocsWithPublicationDates = useMemo(
    () =>
      supersededDocs.map((item) => {
        if (getPublicationDateValue(item)) return item;
        const relatedId = getRelatedStandardId(item);
        return {
          ...item,
          publicationDate:
            otherEditionPublicationDates[relatedId] ?? item.publicationDate,
        };
      }),
    [otherEditionPublicationDates, supersededDocs],
  );
  const replacedDocsWithPublicationDates = useMemo(
    () =>
      replacedDocs.map((item) => {
        if (getPublicationDateValue(item)) return item;
        const relatedId = getRelatedStandardId(item);
        return {
          ...item,
          publicationDate:
            otherEditionPublicationDates[relatedId] ?? item.publicationDate,
        };
      }),
    [otherEditionPublicationDates, replacedDocs],
  );

  const hasRefDocs = relatedDocs.length > 0;
  const hasOtherEditions = supersededDocs.length > 0 || replacedDocs.length > 0;
  const documentTags = firstNonEmptyArray(
    documentData?.summary?.tagIds,
    documentData?.tagIds,
    documentData?.summary?.tags,
    documentData?.tags,
  );

  const referenceNumber =
    documentData?.summary?.referenceNumber ||
    documentData?.reference ||
    documentData?.name ||
    "";

  const categoryName = documentData?.category || "Category";

  const backCategoryId =
    location.state?.categoryId ||
    documentData?.categoryId ||
    documentData?.summary?.categoryId ||
    location.state?.categorySlug ||
    "";

  useEffect(() => {
    // Check if we already have navigation from other sources
    const fromState = location.state?.breadcrumbNavigation;
    const fromDoc = documentData?.navigation;
    if (
      (Array.isArray(fromState) && fromState.length) ||
      (Array.isArray(fromDoc) && fromDoc.length)
    ) {
      if (apiFallbackNav.length > 0) setApiFallbackNav([]);
      return;
    }

    if (!standardId || loading) return;

    let alive = true;
    getDocumentNavigation(standardId).then((nav) => {
      if (alive && Array.isArray(nav) && nav.length > 0) {
        setApiFallbackNav(nav);
      }
    });

    return () => {
      alive = false;
    };
  }, [
    standardId,
    loading,
    documentData,
    location.state?.breadcrumbNavigation,
    getDocumentNavigation,
    apiFallbackNav.length,
  ]);

  const navArr = useMemo(() => {
    const fromState = location.state?.breadcrumbNavigation;
    if (Array.isArray(fromState) && fromState.length) return fromState;
    const fromDoc = documentData?.navigation;
    if (Array.isArray(fromDoc) && fromDoc.length) return fromDoc;
    if (Array.isArray(apiFallbackNav) && apiFallbackNav.length)
      return apiFallbackNav;
    return [];
  }, [
    location.state?.breadcrumbNavigation,
    documentData,
    apiFallbackNav,
  ]);

  const dedupedNav = useMemo(() => dedupeConsecutive(navArr), [navArr]);

  const derivedCategory = useMemo(() => {
    const explicit = normalize(location.state?.categoryTitle);
    if (explicit) return explicit;
    const first = dedupedNav.length ? normalize(dedupedNav[0]?.title) : "";
    return first || normalize(categoryName) || "Category";
  }, [dedupedNav, categoryName, location.state?.categoryTitle]);

  const derivedCategoryId = useMemo(() => {
    if (location.state?.categoryId != null)
      return String(location.state.categoryId);
    for (const n of dedupedNav) {
      if (n?.categoryId != null) return String(n.categoryId);
    }
    return backCategoryId ? String(backCategoryId) : "";
  }, [dedupedNav, backCategoryId, location.state?.categoryId]);
  const currentEditFolderId = useMemo(() => {
    for (const node of [...dedupedNav].reverse()) {
      if (node?.folderId != null) {
        return String(node.folderId);
      }
    }
    return "";
  }, [dedupedNav]);

  const crumbs = useMemo(() => {
    if (!dedupedNav.length) return [];
    const firstTitle = normalize(dedupedNav[0]?.title);
    if (firstTitle && normLower(firstTitle) === normLower(derivedCategory)) {
      return dedupedNav.slice(1);
    }
    return dedupedNav;
  }, [dedupedNav, derivedCategory]);

  const hasVariations = useMemo(() => {
    const rows = [
      ...(variationPendingChange ?? EMPTY_ARRAY),
      ...(derogation ?? EMPTY_ARRAY),
      ...(temporaryVariation ?? EMPTY_ARRAY),
    ];

    return rows.some((row) => clampToStringId(row?.standardId));
  }, [variationPendingChange, derogation, temporaryVariation]);

  const headerTabs = useMemo(() => {
    const tabs = [{ id: "overview", label: "Overview" }];
    if (hasRefDocs) tabs.push({ id: "refdoc", label: "Reference Documents" });
    if (hasOtherEditions) tabs.push({ id: "history", label: "Other Editions" });
    if (hasVariations) tabs.push({ id: "variations", label: "Variations" });
    return tabs;
  }, [hasRefDocs, hasOtherEditions, hasVariations]);

  const editHeaderTabs = useMemo(
    () => [
      { id: "overview", label: "Overview" },
      { id: "refdoc", label: "Reference Documents" },
      { id: "history", label: "Other Editions" },
    ],
    [],
  );

  const activeHeaderTab = useMemo(() => {
    if (pageMode !== "edit") return activeTab;
    if (activeEditPanelKey === "reference-documents") return "refdoc";
    if (activeEditPanelKey === "other-editions") return "history";
    if (activeEditPanelKey === "overview") return "overview";
    return "";
  }, [activeEditPanelKey, activeTab, pageMode]);

  const handleHeaderTabChange = useCallback(
    (nextTab) => {
      if (pageMode !== "edit") {
        setActiveTab(nextTab);
        return;
      }

      if (isDeletedRestoreMode) {
        setActiveEditSection("overview");
        return;
      }

      if (nextTab === "refdoc") {
        setActiveEditSection("reference-documents");
      } else if (nextTab === "history") {
        setActiveEditSection("other-editions");
      } else {
        setActiveEditSection("overview");
      }
    },
    [isDeletedRestoreMode, pageMode],
  );

  const handleAddToDownloadQueue = useCallback(() => {
    if (!standardId || !documentData?.summary) return;

    const summary = documentData.summary;
    const queueItem = {
      id: standardId,
      standardId,
      title:
        summary.title ??
        summary.name ??
        documentData.title ??
        documentData.name ??
        "Untitled",
      referenceNumber:
        summary.referenceNumber ??
        summary.reference ??
        documentData.referenceNumber ??
        documentData.reference ??
        "",
      issueDate:
        summary.issueDate ??
        summary.publicationDate ??
        documentData.issueDate ??
        documentData.publicationDate ??
        null,
      folderId: summary.folderId ?? documentData.folderId ?? null,
    };

    const nextId = getDownloadQueueId(queueItem);
    if (!nextId) return;

    const existing = readDownloadQueueItems();
    const alreadyQueued = existing.some(
      (item) => getDownloadQueueId(item) === nextId,
    );

    setSaveError("");
    if (alreadyQueued) {
      setSaveInfo("This standard is already in the download queue.");
      return;
    }

    window.localStorage.setItem(
      STANDARD_DOWNLOADS_STORAGE_KEY,
      JSON.stringify([...existing, queueItem]),
    );
    setSaveInfo("Added to download queue.");
  }, [documentData, standardId]);

  const openPopup = () => setIsPopupOpen(true);
  const closePopup = () => setIsPopupOpen(false);

  const fetchFiles = useCallback(async () => {
    if (!standardId) return;
    setFilesLoading(true);
    setFilesError("");
    try {
      const data = await getStandardFiles(standardId);
      setFiles(data || {});
    } catch (err) {
      console.error(err);
      setFiles({});
      setFilesError("Could not load files.");
    } finally {
      setFilesLoading(false);
    }
  }, [getStandardFiles, standardId]);

  useEffect(() => {
    fetchFiles();
  }, [standardId]);

  const loadEditSummary = useCallback(async (
    targetStandardId = standardId,
    nextEditPageMode = PAGE_MODE_EDIT_EXISTING,
    issueNumberOverride = "",
  ) => {
    if (!targetStandardId) return;

    setEditLoading(true);
    setEditError("");
    try {
      const response = await getDocumentForEdit(apiBaseUrl, callRef.current, targetStandardId);
      const responseSummary = response?.summary ?? null;
      if (!responseSummary) {
        setEditError("Could not load document edit data.");
        return;
      }
      const summary =
        nextEditPageMode === PAGE_MODE_COPY_ISSUE && issueNumberOverride
          ? { ...responseSummary, issue: issueNumberOverride }
          : responseSummary;
      const nextStates = buildEditStatesFromSummary(
        summary,
        nextEditPageMode,
        documentData?.summary ?? null,
      );
      setEditPageMode(nextEditPageMode);
      setEditSummary(summary);
      setOriginalEditSummary(responseSummary);
      setOverviewState(nextStates.overviewState);
      setOtherEditionsState(nextStates.otherEditionsState);
      setPdssState(nextStates.pdssState);
      setReferenceState(nextStates.referenceState);
    } catch (err) {
      setEditError(err?.response?.data?.message || err?.message || "Could not load document edit data.");
    } finally {
      setEditLoading(false);
    }
  }, [apiBaseUrl, documentData?.summary, standardId]);

  const refreshDocumentView = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setDocumentError("");
    try {
      const response = await getDocument(id);
      const apiMessage =
        typeof response?.message === "string" ? response.message.trim() : "";
      const isFailure = response?.success === false;

      if (isFailure || !response) {
        setDocumentData(null);
        setDocumentError(resolveDocumentLoadError(null, apiMessage, id));
        return;
      }

      setDocumentData(response);
    } catch (err) {
      const status = err?.response?.status;
      const apiMessage =
        typeof err?.response?.data?.message === "string"
          ? err.response.data.message.trim()
          : "";
      setDocumentData(null);
      setDocumentError(resolveDocumentLoadError(status, apiMessage, id));
    } finally {
      setLoading(false);
    }
  }, [getDocument, id]);

  const refreshDocumentNavigation = useCallback(
    async (targetStandardId = standardId) => {
      if (!targetStandardId) {
        setApiFallbackNav([]);
        return [];
      }

      try {
        const nav = await getDocumentNavigation(targetStandardId);
        const nextNavigation = Array.isArray(nav) ? nav : [];
        setApiFallbackNav(nextNavigation);
        return nextNavigation;
      } catch {
        setApiFallbackNav([]);
        return [];
      }
    },
    [getDocumentNavigation, standardId],
  );

  useEffect(() => {
    if (isDeletedRestoreMode && activeEditSection !== "overview") {
      setActiveEditSection("overview");
    }
  }, [activeEditSection, isDeletedRestoreMode]);

  const handleEnterEdit = useCallback(async (
    nextEditPageMode = PAGE_MODE_EDIT_EXISTING,
    issueNumberOverride = "",
  ) => {
    setSaveError("");
    setSaveInfo("");
    setCopyIssueError("");
    setHasSavedEdit(false);
    setAttachmentInfo("");
    setEditSummary(null);
    setOriginalEditSummary(null);
    setOverviewState(null);
    setOtherEditionsState(null);
    setPdssState(null);
    setReferenceState(null);
    setPageMode(EDIT_VIEW_MODE);
    setEditPageMode(nextEditPageMode);
    setActiveEditSection("overview");
    await loadEditSummary(standardId, nextEditPageMode, issueNumberOverride);
  }, [loadEditSummary]);

  const handleCancelEdit = useCallback(() => {
    setPageMode(PAGE_MODE_VIEW);
    setEditPageMode(PAGE_MODE_EDIT_EXISTING);
    setActiveEditSection("overview");
    setEditError("");
    setSaveError("");
    setSaveInfo("");
    setCopyIssueError("");
    setHasSavedEdit(false);
    setAttachmentInfo("");
    setEditSummary(null);
    setOriginalEditSummary(null);
    setOverviewState(null);
    setOtherEditionsState(null);
    setPdssState(null);
    setReferenceState(null);
  }, []);

  const handleReturnToStandard = useCallback(async () => {
    if (
      location.state?.autoEnterEdit ||
      location.state?.editPageMode ||
      location.state?.copiedIssueNumber ||
      location.state?.copiedIssueNotice
    ) {
      const nextState = { ...(location.state ?? {}) };
      delete nextState.autoEnterEdit;
      delete nextState.editPageMode;
      delete nextState.copiedIssueNumber;
      delete nextState.copiedIssueNotice;
      navigate(location.pathname, {
        replace: true,
        state: Object.keys(nextState).length ? nextState : null,
      });
    }
    try {
      await fetchFiles();
    } finally {
      handleCancelEdit();
    }
  }, [fetchFiles, handleCancelEdit, location.pathname, location.state, navigate]);

  const handleSaveEdit = useCallback(async () => {
    setSaveError("");
    setSaveInfo("");
    setCopyIssueError("");
    setAttachmentInfo("");

    if (!standardId || !originalEditSummary || !overviewState || !otherEditionsState || !pdssState || !referenceState) {
      setSaveError("Document edit data is still loading.");
      return;
    }

    const payload = buildUpdatePayload({
      pageMode: editPageMode,
      originalSummary: originalEditSummary,
      overviewState,
      otherEditionsState,
      pdssState,
      referenceState,
    });

    if (!Object.keys(payload).length) {
      setSaveInfo("No changes to save.");
      return;
    }

    setSaveLoading(true);
    const saveOverlayStart = Date.now();
    try {
      const wasDeletedRestoreMode = isDeletedRestoreMode;
      await waitForNextPaint();
      const duplicateIssue = await findDuplicateStandardIssue(
        apiBaseUrl,
        callRef.current,
        {
          referenceNumber: overviewState?.refNo,
          issue: overviewState?.issueNumber,
          excludeStandardId: standardId,
        },
      );
      if (duplicateIssue) {
        setSaveError(DUPLICATE_ISSUE_MESSAGE);
        return;
      }

      const response = await updateStandard(apiBaseUrl, callRef.current, standardId, payload);
      const summary = response?.summary ?? null;
      if (summary) {
        const nextStates = mergeEditStatesWithResolvedNumbers(
          buildEditStatesFromSummary(summary, editPageMode, overviewState),
          {
            otherEditionsState,
            referenceState,
          },
        );
        setEditSummary(summary);
        setOriginalEditSummary(summary);
        setOverviewState(nextStates.overviewState);
        setOtherEditionsState(nextStates.otherEditionsState);
        setPdssState(nextStates.pdssState);
        setReferenceState(nextStates.referenceState);
        baselineCapturedRef.current = true;
        setBaselineStr(JSON.stringify(nextStates));
        setDebouncedHasChanges(false);
      } else {
        baselineCapturedRef.current = true;
        setBaselineStr(
          JSON.stringify({ overviewState, otherEditionsState, pdssState, referenceState }),
        );
        setDebouncedHasChanges(false);
      }
      await refreshDocumentView();
      await fetchFiles();
      setHasSavedEdit(true);
      setSaveInfo(
        wasDeletedRestoreMode && summary && !isDeletedSummary(summary)
          ? "Deleted status removed. Non-admin users can view this standard again."
          : "Changes saved.",
      );
    } catch (err) {
      const status = err?.response?.status;
      const apiMessage =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message;
      if (status === 409) {
        setSaveError(DUPLICATE_ISSUE_MESSAGE);
      } else if (status === 404) {
        setSaveError(apiMessage || "This standard no longer exists.");
      } else if (status === 403) {
        setSaveError(apiMessage || "You do not have permission to update this standard.");
      } else if (status === 400) {
        setSaveError(apiMessage || "The standard could not be updated because some values are invalid.");
      } else {
        setSaveError(apiMessage || "The standard could not be updated.");
      }
    } finally {
      await waitForMinimumOverlayTime(saveOverlayStart);
      setSaveLoading(false);
    }
  }, [
    apiBaseUrl,
    fetchFiles,
    originalEditSummary,
    otherEditionsState,
    overviewState,
    pdssState,
    referenceState,
    refreshDocumentView,
    standardId,
    isDeletedRestoreMode,
    editPageMode,
  ]);

  const handleCopyIssueEdit = useCallback(async () => {
    setCopyIssueError("");
    setSaveError("");
    setSaveInfo("");
    setAttachmentInfo("");

    if (isDeletedEditStatus) {
      setCopyIssueError("Restore the status before creating a new issue from this standard.");
      return;
    }

    if (!standardId) {
      setCopyIssueError("Copy Issue is only available for saved standards.");
      return;
    }

    setCopyIssueLoading(true);
    try {
      const nextIssueNumber = await getNextIssueNumberForReference(
        apiBaseUrl,
        callRef.current,
        {
          referenceNumber: documentData?.summary?.referenceNumber,
          sourceIssue: documentData?.summary?.issue,
        },
      );
      const response = await createNewIssue(apiBaseUrl, callRef.current, standardId);
      const createdStandardId = resolveCreatedStandardId(response, standardId);
      if (!createdStandardId) {
        setCopyIssueError("A new issue could not be created.");
        return;
      }

      const copiedIssueNumber =
        nextIssueNumber ||
        resolveCopiedIssueNumber(
          documentData?.summary?.issue,
          response?.summary?.issue,
        );

      navigate(`/document/${createdStandardId}`, {
        state: {
          ...(location.state ?? {}),
          autoEnterEdit: true,
          editPageMode: PAGE_MODE_COPY_ISSUE,
          copiedIssueNumber,
          copiedIssueNotice: "New issue created. Review the issue number and save your changes.",
        },
      });
    } catch (err) {
      const status = err?.response?.status;
      const apiMessage =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message;
      if (status === 404) {
        setCopyIssueError(apiMessage || "The source standard could not be found.");
      } else if (status === 403) {
        setCopyIssueError(apiMessage || "You do not have permission to copy this standard.");
      } else if (status === 400) {
        setCopyIssueError(apiMessage || "A new issue could not be created from this standard.");
      } else {
        setCopyIssueError(apiMessage || "A new issue could not be created.");
      }
    } finally {
      setCopyIssueLoading(false);
    }
  }, [
    apiBaseUrl,
    documentData?.summary?.issue,
    documentData?.summary?.referenceNumber,
    isDeletedEditStatus,
    location.state,
    navigate,
    standardId,
  ]);

  const editPayload = useMemo(() => {
    if (
      pageMode !== EDIT_VIEW_MODE ||
      !originalEditSummary ||
      !overviewState ||
      !otherEditionsState ||
      !pdssState ||
      !referenceState
    ) {
      return {};
    }

    return buildUpdatePayload({
      pageMode: editPageMode,
      originalSummary: originalEditSummary,
      overviewState,
      otherEditionsState,
      pdssState,
      referenceState,
    });
  }, [
    otherEditionsState,
    originalEditSummary,
    overviewState,
    pageMode,
    pdssState,
    referenceState,
    editPageMode,
  ]);

  // --- Debounced save button visibility ---
  // We capture a "baseline" snapshot of the section states 500ms after entering
  // edit mode.  This gives Quill (and async lookups) time to normalise values.
  // After that, we compare the live states against this baseline – not against
  // the raw API summary – so Quill HTML‑wrapping cannot cause false positives.

  const baselineCapturedRef = useRef(false);
  const [baselineStr, setBaselineStr] = useState(null);
  const [debouncedHasChanges, setDebouncedHasChanges] = useState(false);

  // Reset the baseline whenever we enter / leave edit mode
  useEffect(() => {
    baselineCapturedRef.current = false;
    setBaselineStr(null);
    setDebouncedHasChanges(false);
  }, [pageMode]);

  const saveOverlay =
    saveLoading && typeof document !== "undefined"
      ? createPortal(
          <div className={styles.downloadOverlay}>
            <div className={styles.progressBox}>
              <p>Saving file... 0%</p>
              <div className={styles.progressBar}>
                <div className={styles.progressFill} style={{ width: "0%" }} />
              </div>
            </div>
          </div>,
          document.body,
        )
      : null;

  // Capture the baseline once, 500ms after all states are available
  useEffect(() => {
    if (pageMode !== EDIT_VIEW_MODE || editLoading || baselineCapturedRef.current) return;
    if (!overviewState || !otherEditionsState || !pdssState || !referenceState) return;

    const timer = setTimeout(() => {
      if (!baselineCapturedRef.current) {
        baselineCapturedRef.current = true;
        setBaselineStr(
          JSON.stringify({ overviewState, otherEditionsState, pdssState, referenceState }),
        );
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [pageMode, editLoading, overviewState, otherEditionsState, pdssState, referenceState]);

  useEffect(() => {
    if (pageMode !== EDIT_VIEW_MODE || editLoading || !otherEditionsState || !referenceState) {
      return;
    }

    const unresolvedStandardIds = collectEditStateUnresolvedReferenceIds({
      otherEditionsState,
      referenceState,
    });
    if (!apiBaseUrl || !unresolvedStandardIds.length) return;

    const currentRequestId = resolveReferenceRequestIdRef.current + 1;
    resolveReferenceRequestIdRef.current = currentRequestId;
    let alive = true;

    resolveReferenceNumbers(apiBaseUrl, callRef.current, unresolvedStandardIds)
      .then((resolvedMap) => {
        if (!alive || resolveReferenceRequestIdRef.current !== currentRequestId) return;

        const nextStates = applyResolvedReferenceNumbersToEditStates(
          resolvedEditStatesRef.current,
          resolvedMap,
        );
        if (!nextStates) return;

        const refChanged = !sameStateValue(resolvedEditStatesRef.current.referenceState, nextStates.referenceState);
        const edChanged = !sameStateValue(resolvedEditStatesRef.current.otherEditionsState, nextStates.otherEditionsState);

        if (refChanged) {
          setReferenceState(nextStates.referenceState);
        }
        if (edChanged) {
          setOtherEditionsState(nextStates.otherEditionsState);
        }

        if ((refChanged || edChanged) && baselineCapturedRef.current) {
          baselineCapturedRef.current = false;
          setBaselineStr(null);
        }
      })
      .catch(() => {
        // Keep fallback IDs visible when a lookup cannot be resolved.
      });

    return () => {
      alive = false;
    };
  }, [apiBaseUrl, editLoading, otherEditionsState, pageMode, referenceState]);

  // Serialise current states so every keystroke produces a new string
  const currentStatesStr = useMemo(
    () => JSON.stringify({ overviewState, otherEditionsState, pdssState, referenceState }),
    [overviewState, otherEditionsState, pdssState, referenceState],
  );

  // Debounce: 2s after the last change, show save.  Revert → hide immediately.
  useEffect(() => {
    // Before baseline is captured, never show save
    if (!baselineStr || currentStatesStr === baselineStr) {
      setDebouncedHasChanges(false);
      return;
    }

    const timer = setTimeout(() => setDebouncedHasChanges(true), 2000);
    return () => clearTimeout(timer);
  }, [currentStatesStr, baselineStr]);

  const hasPendingMoveChanges =
    pageMode === EDIT_VIEW_MODE && Boolean(baselineStr) && currentStatesStr !== baselineStr;
  const canSaveEdit = debouncedHasChanges;
  const isCopyIssueEditMode = editPageMode === PAGE_MODE_COPY_ISSUE;
  const canReturnToStandard =
    isCopyIssueEditMode ||
    (hasSavedEdit && !saveLoading && Boolean(baselineStr) && currentStatesStr === baselineStr);

  const handleOverviewStateChange = useCallback((next) => {
    const nextState = { ...DEFAULT_OVERVIEW_STATE, ...(next ?? {}) };
    setOverviewState((prev) => (sameStateValue(prev, nextState) ? prev : nextState));
  }, []);

  const handleOtherEditionsStateChange = useCallback((next) => {
    const nextState = {
      documents: Array.isArray(next?.documents) ? next.documents : [],
      selectedIds: Array.isArray(next?.selectedIds) ? next.selectedIds : [],
    };
    setOtherEditionsState((prev) => (sameStateValue(prev, nextState) ? prev : nextState));
  }, []);

  const handlePdssStateChange = useCallback((next) => {
    const nextState = { ...DEFAULT_PDSS_STATE, ...(next ?? {}) };
    setPdssState((prev) => (sameStateValue(prev, nextState) ? prev : nextState));
  }, []);

  const handleReferenceStateChange = useCallback((next) => {
    const nextState = { ...DEFAULT_REFERENCE_STATE, ...(next ?? {}) };
    setReferenceState((prev) => (sameStateValue(prev, nextState) ? prev : nextState));
  }, []);

  const handleMoveLocationBlocked = useCallback(() => {
    setSaveInfo("");
    setCopyIssueError("");
    setAttachmentInfo("");
    setSaveError("Save your changes before moving this standard to another folder.");
  }, []);

  const handleMoveLocationSuccess = useCallback(async () => {
    if (!standardId) return;

    setSaveError("");
    setCopyIssueError("");
    setAttachmentInfo("");
    setSaveInfo("");

    if (location.state) {
      const nextState = { ...(location.state ?? {}) };
      delete nextState.breadcrumbNavigation;
      delete nextState.categoryId;
      delete nextState.categoryTitle;
      delete nextState.categorySlug;
      navigate(location.pathname, {
        replace: true,
        state: Object.keys(nextState).length ? nextState : null,
      });
    }

    await Promise.allSettled([
      loadEditSummary(standardId, editPageMode),
      refreshDocumentView(),
      refreshDocumentNavigation(standardId),
    ]);

    baselineCapturedRef.current = false;
    setBaselineStr(null);
    setDebouncedHasChanges(false);

    setSaveInfo("Standard moved.");
  }, [
    editPageMode,
    loadEditSummary,
    location.pathname,
    location.state,
    navigate,
    refreshDocumentNavigation,
    refreshDocumentView,
    standardId,
  ]);

  const onToggleFavourite = async () => {
    if (!apiBaseUrl || !standardId || favBusy) return;

    const next = !isFav;
    setFavBusy(true);

    try {
      if (next) {
        await favoriteStandard(apiBaseUrl, callRef.current, standardId);
        setIsFav(true);
        setDocumentData((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            favorite: true,
            summary: prev.summary
              ? { ...prev.summary, favorite: true, favorited: true }
              : prev.summary,
          };
        });
      } else {
        await removeFavoriteStandard(apiBaseUrl, callRef.current, standardId);
        setIsFav(false);
        setDocumentData((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            favorite: false,
            summary: prev.summary
              ? { ...prev.summary, favorite: false, favorited: false }
              : prev.summary,
          };
        });
      }
    } finally {
      setFavBusy(false);
    }
  };

  useEffect(() => {
    if (activeTab === "refdoc" && !hasRefDocs) {
      setActiveTab("overview");
      return;
    }
    if (activeTab === "history" && !hasOtherEditions) {
      setActiveTab("overview");
      return;
    }
    if (activeTab === "variations" && !hasVariations) {
      setActiveTab("overview");
    }
  }, [activeTab, hasRefDocs, hasOtherEditions, hasVariations]);

  useEffect(() => {
    if (pageMode !== EDIT_VIEW_MODE) {
      setShowStickyEditActions(false);
      return;
    }

    const onScroll = () => {
      setShowStickyEditActions(window.scrollY > 24);
    };

    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [pageMode]);

  useEffect(() => {
    if (!location.state?.autoEnterEdit) return;
    if (!standardId || loading || pageMode !== PAGE_MODE_VIEW) return;

    handleEnterEdit(
      location.state?.editPageMode || PAGE_MODE_EDIT_EXISTING,
      location.state?.copiedIssueNumber || "",
    );

    const nextState = { ...(location.state ?? {}) };
    delete nextState.autoEnterEdit;
    delete nextState.editPageMode;
    delete nextState.copiedIssueNumber;
    navigate(location.pathname, {
      replace: true,
      state: Object.keys(nextState).length ? nextState : null,
    });
  }, [
    handleEnterEdit,
    loading,
    location.pathname,
    location.state,
    location.state?.autoEnterEdit,
    location.state?.editPageMode,
    navigate,
    pageMode,
    standardId,
  ]);

  const handleDeleteConfirm = useCallback(async () => {
    setDeleteError("");
    setSaveError("");
    setSaveInfo("");
    setCopyIssueError("");
    setAttachmentInfo("");

    if (!standardId) {
      setDeleteError("Delete is only available for saved standards.");
      return;
    }

    setDeleteLoading(true);
    try {
      await deleteStandard(apiBaseUrl, callRef.current, standardId);
      await refreshDocumentView();
      setShowDeleteModal(false);
      setSaveInfo(
        "Standard deleted. Only admins can view it until the status is changed away from deleted.",
      );
    } catch (err) {
      const status = err?.response?.status;
      const apiMessage =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message;

      if (status === 404) {
        await refreshDocumentView();
        setShowDeleteModal(false);
        setSaveInfo(
          apiMessage ||
            "This standard is already deleted. Only admins can view it until the status changes.",
        );
      } else if (status === 403) {
        setDeleteError(apiMessage || "You do not have permission to delete this standard.");
      } else if (status === 400) {
        setDeleteError(apiMessage || "This standard could not be deleted.");
      } else {
        setDeleteError(apiMessage || "This standard could not be deleted.");
      }
    } finally {
      setDeleteLoading(false);
    }
  }, [apiBaseUrl, refreshDocumentView, standardId]);

  if (loading) {
    return (
      <div className={styles.documentPage}>
        <ProgressSpinnerComponent />
      </div>
    );
  }

  if (!documentData) {
    return <p style={{ padding: "2rem" }}>{documentError}</p>;
  }

  return (
    <div className={styles.documentPage}>
      {showDeleteModal ? (
        <DeleteStandardPopUp
          isOpen={showDeleteModal}
          message="Are you sure you want to delete this standard?"
          detail="This is a soft delete. Only admins will be able to view the standard until the status is changed away from deleted."
          busy={deleteLoading}
          errorMessage={deleteError}
          onConfirm={handleDeleteConfirm}
          onCancel={() => {
            if (deleteLoading) return;
            setDeleteError("");
            setShowDeleteModal(false);
          }}
        />
      ) : null}
      {isPopupOpen && (
        <AttachmentPopup
          standardId={standardId}
          handleClose={closePopup}
          onUploaded={async () => {
            await fetchFiles();
            if (pageMode === "edit") {
              setAttachmentInfo(
                "Upload complete. The attachment is already saved, so you do not need to click Save.",
              );
            }
          }}
        />
      )}

      <div className={styles.documentInner}>
        <nav className={styles.breadcrumb} aria-label="Breadcrumb">
          <Link to="/steering-groups">Steering Groups</Link>
          <span className={styles.breadcrumbArrow}>&rsaquo;</span>

          {derivedCategoryId ? (
            <Link
              className={styles.crumbLink}
              to={`/standards-category-specific?cid=${encodeURIComponent(derivedCategoryId)}`}
            >
              {derivedCategory}
            </Link>
          ) : (
            <span className={styles.breadcrumbCurrent}>{derivedCategory}</span>
          )}

          {crumbs.map((n, idx) => {
            const label = normalize(n?.title) || "Folder";
            const cid =
              n?.categoryId != null ? String(n.categoryId) : derivedCategoryId;
            const fid = n?.folderId != null ? String(n.folderId) : "";

            const link =
              cid && fid
                ? `/standards-category-specific?cid=${encodeURIComponent(cid)}&fid=${encodeURIComponent(fid)}`
                : cid
                  ? `/standards-category-specific?cid=${encodeURIComponent(cid)}`
                  : fid
                    ? `/standards-category-specific?fid=${encodeURIComponent(fid)}`
                    : "/standards-category-specific";

            return (
              <React.Fragment
                key={`${n.folderId ?? n.categoryId ?? "x"}-${label}-${idx}`}
              >
                <span className={styles.breadcrumbArrow}>&rsaquo;</span>
                <Link className={styles.crumbLink} to={link}>
                  {label}
                </Link>
              </React.Fragment>
            );
          })}

          <span className={styles.breadcrumbArrow}>&rsaquo;</span>
          <span className={styles.breadcrumbCurrent}>
            {referenceNumber || documentData?.name || "Document"}
          </span>
        </nav>

        <DocumentHeader
          documentData={documentData?.summary}
          isFav={isFav}
          favResolved={favResolved}
          onToggleFavourite={onToggleFavourite}
          isEditMode={pageMode === EDIT_VIEW_MODE}
          canSave={canSaveEdit}
          canReturnToStandard={canReturnToStandard}
          onEnterEdit={handleEnterEdit}
          onSave={handleSaveEdit}
          onCopyIssue={handleCopyIssueEdit}
          onCancel={handleCancelEdit}
          onReturnToStandard={handleReturnToStandard}
          onDelete={() => {
            setDeleteError("");
            setShowDeleteModal(true);
          }}
          saveLoading={saveLoading}
          copyIssueLoading={copyIssueLoading}
          deleteLoading={deleteLoading}
          canCopyIssue={Boolean(standardId) && !isDeletedRestoreMode}
          canDelete={!isDeletedDocument}
          hideEditActions={showStickyEditActions}
          tabsDisabled={isDeletedRestoreMode}
          activeTab={activeHeaderTab}
          onTabChange={handleHeaderTabChange}
          favBusy={favBusy}
          onAddToDownloadQueue={
            pageMode === PAGE_MODE_VIEW ? handleAddToDownloadQueue : undefined
          }
          addToDownloadQueueDisabled={!standardId}
          tabs={pageMode === "edit" ? editHeaderTabs : headerTabs}
        />

        {saveError ? <p className={styles.saveError}>{saveError}</p> : null}
        {saveInfo ? <p className={styles.saveInfo}>{saveInfo}</p> : null}
        {pageMode === EDIT_VIEW_MODE && isDeletedRestoreMode ? (
          <p className={styles.deletedNotice}>
            Deleted standards remain visible only to admins. Change the status away from deleted and save to restore visibility for non-admin users.
          </p>
        ) : null}

        {pageMode === EDIT_VIEW_MODE ? (
          <>
            {editLoading ? <p className={styles.editInfo}>Loading existing standard...</p> : null}
            {editError ? <p className={styles.editError}>{editError}</p> : null}
            {copyIssueError ? <p className={styles.editError}>{copyIssueError}</p> : null}

            <div className={styles.editLayout}>
              <NewStandSideBar
                activeItem={activeEditPanelKey}
                onItemChange={setActiveEditSection}
                title={isDeletedRestoreMode ? "Restore Standard" : "Edit Standard"}
                disabled={isDeletedRestoreMode}
              />

              <div className={styles.editMain}>
                <Suspense fallback={<ProgressSpinnerComponent />}>
                  {!editLoading && ActiveEditPanel ? (
                    activeEditPanelKey === "overview" ? (
                      <ActiveEditPanel
                        persistedState={overviewState}
                        onStateChange={handleOverviewStateChange}
                        isReferenceReadOnly={false}
                        isIssueReadOnly={false}
                        isDeletedRestoreMode={isDeletedRestoreMode}
                        attachmentsEnabled={Boolean(standardId)}
                        standardId={standardId}
                        documentAttachments={files?.documents ?? []}
                        referenceAttachments={files?.references ?? []}
                        briefingMaterials={files?.briefingMaterials ?? []}
                        attachmentsLoading={filesLoading}
                        attachmentsError={filesError}
                        attachmentInfo={attachmentInfo}
                        onOpenAttachmentPopup={openPopup}
                      />
                    ) : activeEditPanelKey === "other-editions" ? (
                      <ActiveEditPanel
                        persistedState={otherEditionsState}
                        onStateChange={handleOtherEditionsStateChange}
                      />
                    ) : activeEditPanelKey === "reference-documents" ? (
                      <ActiveEditPanel
                        persistedState={referenceState}
                        onStateChange={handleReferenceStateChange}
                        standardId={standardId}
                        currentCategoryId={derivedCategoryId}
                        currentFolderId={currentEditFolderId}
                        canChangeLocation={hasAdminRights && Boolean(standardId)}
                        hasPendingChanges={hasPendingMoveChanges}
                        onMoveBlocked={handleMoveLocationBlocked}
                        onMoveSuccess={handleMoveLocationSuccess}
                      />
                    ) : (
                      <ActiveEditPanel
                        persistedState={pdssState}
                        onStateChange={handlePdssStateChange}
                      />
                    )
                  ) : null}
                </Suspense>
              </div>
            </div>

            <div
              className={`${styles.editFooterActions} ${
                showStickyEditActions ? styles.editFooterActionsVisible : ""
              }`}
            >
              <button
                type="button"
                className={styles.editFooterCancelBtn}
                onClick={handleCancelEdit}
                disabled={saveLoading || copyIssueLoading || deleteLoading}
              >
                Cancel
              </button>
              <button
                type="button"
                className={styles.editFooterCopyBtn}
                onClick={handleCopyIssueEdit}
                disabled={!standardId || saveLoading || copyIssueLoading || deleteLoading || isDeletedRestoreMode}
              >
                {copyIssueLoading ? "Copying..." : "Copy Issue"}
              </button>
              {canSaveEdit ? (
                <button
                  type="button"
                  className={styles.editFooterSaveBtn}
                  onClick={handleSaveEdit}
                  disabled={saveLoading || copyIssueLoading || deleteLoading}
                >
                  {saveLoading ? "Saving..." : "Save"}
                </button>
              ) : null}
              <button
                type="button"
                className={`${styles.editFooterSaveBtn} ${styles.editFooterReturnBtn}`}
                onClick={handleReturnToStandard}
                disabled={saveLoading || copyIssueLoading || deleteLoading}
              >
                Return To Standard
              </button>
            </div>
          </>
        ) : (

          <div className={styles.documentBody}>
            <div className={styles.documentMain}>
              {activeTab === "overview" && (
                <>
                  <Synopsis data={documentData} hasVariations={hasVariations} />
                  <ChallengeStandard />
                </>
              )}

              {activeTab === "refdoc" && hasRefDocs && (
                <RefDoc related={relatedDocs} />
              )}

              {activeTab === "history" && hasOtherEditions && (
                <OtherEditions
                  superseded={supersededDocsWithPublicationDates}
                  replaced={replacedDocsWithPublicationDates}
                />
              )}

              {activeTab === "variations" && hasVariations && (
                <DocumentVariations
                  standardId={standardId}
                  standardNumber={variationsStandardNumber}
                  issueNo={variationsIssueNo}
                />
              )}
            </div>

            {activeTab !== "variations" && (
              <aside className={styles.documentSidebar}>
                <DocumentsTab
                  summary={documentData?.summary}
                  standardId={standardId}
                  openPopup={openPopup}
                  documents={files?.documents ?? []}
                  references={files?.references ?? []}
                  filesLoading={filesLoading}
                  filesError={filesError}
                />

                {activeTab === "overview" && (
                  <>
                    <Modules data={documentData} />
                    <VideoBriefing
                      briefingMaterials={files?.briefingMaterials ?? []}
                      filesLoading={filesLoading}
                      filesError={filesError}
                    />
                  </>
                )}

                <OwnerTab data={documentData} />
                <TagsWidget tags={documentTags} />
              </aside>
            )}


          </div>
        )}
      </div>
      {saveOverlay}
    </div>
  );
}
